/**
 * PAYG (Pay-As-You-Go) Utilities
 *
 * Helper functions for PAYG mode logic.
 * Eligibility is determined by the backend's `eligible_to_pay_as_you_go` flag
 * on each PaymentMethod. The backend also receives `payg_context` in the
 * /combined request so it can return only appropriate available options.
 */

import type { PaymentMethod } from '../types';
import { PURCHASE_FLOW_TEXT_KEYS } from './constants';

/** Maps payment method kind to the PAYG ineligibility text key (existing translations) */
const PAYG_INELIGIBLE_TEXT_KEY_MAP: Record<string, string> = {
  cc: PURCHASE_FLOW_TEXT_KEYS.PAYG_INELIGIBLE_CARD,
  pp: PURCHASE_FLOW_TEXT_KEYS.PAYG_INELIGIBLE_PAYPAL,
  gp: PURCHASE_FLOW_TEXT_KEYS.PAYG_INELIGIBLE_GOOGLE_PAY,
  ap: PURCHASE_FLOW_TEXT_KEYS.PAYG_INELIGIBLE_APPLE_PAY,
  am: PURCHASE_FLOW_TEXT_KEYS.PAYG_INELIGIBLE_APPLE_PAY
};

/**
 * Get the text key for a PAYG-ineligible payment method.
 * Uses the backend's `eligible_to_pay_as_you_go` flag.
 * Returns null if the method is eligible.
 *
 * The returned key should be rendered via `renderText(key)` by the consumer.
 */
export const getPaygIneligibilityTextKey = (method: PaymentMethod | null): string | null => {
  if (!method) return null;
  if (method.eligibleToPayAsYouGo !== false) return null;

  return PAYG_INELIGIBLE_TEXT_KEY_MAP[method.kind] || PURCHASE_FLOW_TEXT_KEYS.PAYG_INELIGIBLE_CARD;
};
